import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Instagram, Youtube, Facebook } from "lucide-react";
import { useCompanySettings } from "@/hooks/useCompanySettings";

const Footer = () => {
  const company = useCompanySettings();

  // Social links: show when not loaded (fallback) or when loaded and URL is present
  const showInstagram = !company.loaded ? true : Boolean(company.instagram_url);
  const showYoutube   = !company.loaded ? false : Boolean(company.youtube_url);
  const showFacebook  = !company.loaded ? false : Boolean(company.facebook_url);

  const instagramUrl = company.loaded
    ? company.instagram_url
    : "https://www.instagram.com/konyapmuhendislik/";

  return (
    <footer className="bg-[#111111] py-16 md:py-32" data-testid="footer">
      <div className="footer-shell max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="footer-grid grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12 mb-16">
          <div className="footer-brand col-span-2 md:col-span-1">
            <div className="mb-4">
              <img
                src={company.logo_url || "/images/1-logo/dikey/Beyaz.png"}
                alt="Konyap Mühendislik — Konya İnşaat ve Villa Projeleri"
                className="h-10 md:h-16 w-auto object-contain"
                loading="lazy"
                decoding="async"
              />
            </div>
            <p className="text-[#86868B] text-sm leading-relaxed">
              Güçlü temeller, güçlü yaşamlar. Konya'nın prestij adresi.
            </p>
          </div>

          <div className="footer-links-group">
            <h3 className="text-[#F5F5F7] font-medium text-base md:text-lg mb-4 md:mb-6">
              Hızlı Bağlantılar
            </h3>
            <ul className="space-y-3">
              <li>
                <Link to="/" className="text-[#86868B] hover:text-[#C5A059] text-sm transition-colors" data-testid="footer-link-ana-sayfa">
                  Ana Sayfa
                </Link>
              </li>
              <li>
                <Link to="/hakkimizda" className="text-[#86868B] hover:text-[#C5A059] text-sm transition-colors" data-testid="footer-link-hakkimizda">
                  Hakkımızda
                </Link>
              </li>
              <li>
                <Link to="/hizmetler" className="text-[#86868B] hover:text-[#C5A059] text-sm transition-colors" data-testid="footer-link-hizmetler">
                  Hizmetler
                </Link>
              </li>
              <li>
                <Link to="/projeler" className="text-[#86868B] hover:text-[#C5A059] text-sm transition-colors" data-testid="footer-link-projeler">
                  Projeler
                </Link>
              </li>
              <li>
                <Link to="/iletisim" className="text-[#86868B] hover:text-[#C5A059] text-sm transition-colors" data-testid="footer-link-iletisim">
                  İletişim
                </Link>
              </li>
              <li>
                <Link to="/dreams" className="font-signature text-lg signature-gold" data-testid="footer-link-dreams">
                  Dreams
                </Link>
              </li>
            </ul>
          </div>

          <div className="footer-services-group">
            <h3 className="text-[#F5F5F7] font-medium text-base md:text-lg mb-4 md:mb-6">
              Hizmetlerimiz
            </h3>
            <ul className="space-y-3">
              <li className="text-[#86868B] text-sm">Konut Projeleri</li>
              <li className="text-[#86868B] text-sm">Ticari Yapılar</li>
              <li className="text-[#86868B] text-sm">İnşaat Mühendisliği</li>
              <li className="text-[#86868B] text-sm">Proje Yönetimi</li>
              <li className="text-[#86868B] text-sm">Danışmanlık</li>
            </ul>
          </div>

          <div className="footer-contact col-span-2 md:col-span-1">
            <h3 className="text-[#F5F5F7] font-medium text-base md:text-lg mb-4 md:mb-6">
              İletişim
            </h3>
            <ul className="space-y-4">
              {company.address && (
                <li className="flex items-start gap-3 text-[#86868B] text-sm">
                  <MapPin size={18} className="text-[#C5A059] mt-1 flex-shrink-0" />
                  <span>{company.address}</span>
                </li>
              )}
              {company.phone && (
                <li className="flex items-center gap-3 text-[#86868B] text-sm">
                  <Phone size={18} className="text-[#C5A059] flex-shrink-0" />
                  <a
                    href={`tel:${company.phone.replace(/\s/g, "")}`}
                    className="hover:text-[#C5A059] transition-colors"
                  >
                    {company.phone}
                  </a>
                </li>
              )}
              {company.email && (
                <li className="flex items-center gap-3 text-[#86868B] text-sm">
                  <Mail size={18} className="text-[#C5A059] flex-shrink-0" />
                  <a
                    href={`mailto:${company.email}`}
                    className="hover:text-[#C5A059] transition-colors break-all"
                  >
                    {company.email}
                  </a>
                </li>
              )}
              {showInstagram && (
                <li className="flex items-center gap-3 text-[#86868B] text-sm">
                  <Instagram size={18} className="text-[#C5A059] flex-shrink-0" />
                  <a
                    href={instagramUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-[#C5A059] transition-colors"
                    data-testid="footer-instagram-link"
                  >
                    {instagramUrl.replace(/^https?:\/\/(www\.)?instagram\.com\/?/, "@").replace(/\/$/, "") || "Instagram"}
                  </a>
                </li>
              )}
              {showYoutube && (
                <li className="flex items-center gap-3 text-[#86868B] text-sm">
                  <Youtube size={18} className="text-[#C5A059] flex-shrink-0" />
                  <a
                    href={company.youtube_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-[#C5A059] transition-colors"
                  >
                    YouTube
                  </a>
                </li>
              )}
              {showFacebook && (
                <li className="flex items-center gap-3 text-[#86868B] text-sm">
                  <Facebook size={18} className="text-[#C5A059] flex-shrink-0" />
                  <a
                    href={company.facebook_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-[#C5A059] transition-colors"
                  >
                    Facebook
                  </a>
                </li>
              )}
            </ul>
          </div>
        </div>

        <div className="footer-bottom border-t border-white/10 pt-8">
          <div className="footer-bottom-row flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-[#555555] text-sm">
              © {new Date().getFullYear()} {company.company_name}. Tüm hakları saklıdır.
            </p>
            <div className="flex gap-6">
              <Link to="/gizlilik-politikasi" className="text-[#86868B] hover:text-[#C5A059] text-sm transition-colors">
                Gizlilik Politikası
              </Link>
              <Link to="/kullanim-sartlari" className="text-[#86868B] hover:text-[#C5A059] text-sm transition-colors">
                Kullanım Şartları
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
